import * as React from "react";
import { useEffect, useState } from "react";
import { mergeStyleSets, Modal, Stack } from "@fluentui/react";
import HeaderPanel from "../layout/HeaderPanel";
import FooterPanel from "../layout/FooterPanel";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { fetchDocumentComponents } from "../../api/object/ObjectComponentApi";
import { SearchBar } from "../generic/searchBar/SearchBar";
import { WithApiFileLocationProvider } from "../document/WithApiFileLocationProvider";
import { fetchDocumentList } from "../../api/document/Location";
import { mapDocumentPartsToFileLocations } from "../document/fileLocationMapper";
import { DocumentLocationExplorer } from "../document/DocumentLocationExplorer";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
import { DocumentOutline } from "../document/DocumentOutline";
import { DocumentPartTreeSelection } from "../cpm/DocumentPartTree";
import { mergeStyles } from "@fluentui/react/lib/Styling";
import { ComponentReference } from "../../api/models/component-reference";
import { getHTMLElementAsButtonProps } from "../../common/accessibility/HTMLElementAsButtonARIA";

export interface ObjectComponentReferenceSelectorProps {
  onComponentSelected: (componentInfoItem?: ComponentReference) => void;
}

interface ObjectComponentReferenceSelectorState {
  selectedObjectComponentInfoItem?: ComponentReference;
  page: "folders" | "outline" | "components";
  loaded: boolean;
  itemSelected: FlatPathTreeItem;
  outlineItemSelected: DocumentPartTreeSelection;
}

const objectComponentReferenceSelectorInitialState: ObjectComponentReferenceSelectorState = {
  page: "folders",
  loaded: false,
  itemSelected: null,
  outlineItemSelected: null,
};

export const ObjectComponentReferenceSelector: React.FunctionComponent<ObjectComponentReferenceSelectorProps> = (
  props
) => {
  const { onComponentSelected } = props;
  const [state, setState] = useState<ObjectComponentReferenceSelectorState>(
    objectComponentReferenceSelectorInitialState
  );
  const { selectedObjectComponentInfoItem, page, itemSelected, outlineItemSelected, loaded } = state;

  function onSelectPart(item: FlatPathTreeItem) {
    setState((s) => ({ ...s, itemSelected: item }));
  }

  const onOutlineItemClick = (selection: DocumentPartTreeSelection) => {
    setState((s) => ({ ...s, outlineItemSelected: selection }));
  };

  const scrollableContentStyle = mergeStyleSets({
    scrollableContent: mergeStyles({
      maxHeight: "calc(100vh - 62px)",
    }),
  });

  let selectButtonText: string;
  if (page === "folders") {
    selectButtonText = formatMessage("cof.documentproperties.viewoutline");
  } else if (page === "outline") {
    selectButtonText = formatMessage("cof.createnewobject.viewcomponents");
  } else {
    selectButtonText = formatMessage("cof.base.select");
  }

  let selectButtonDisabled: boolean;
  if (page === "folders") {
    selectButtonDisabled = !itemSelected || itemSelected.document.folder;
  } else if (page === "outline") {
    selectButtonDisabled = !outlineItemSelected;
  } else {
    selectButtonDisabled = !selectedObjectComponentInfoItem;
  }

  return (
    <Modal
      titleAriaId={"titleId"}
      isOpen={true}
      scrollableContentClassName={scrollableContentStyle.scrollableContent}
      styles={{
        main: {
          minHeight: "100%",
          minWidth: "100%",
        },
      }}
    >
      <HeaderPanel applyPadding headerRowJustifyContent={"space-between"}>
        {formatMessage("cof.createnewobject.componentselection")}
      </HeaderPanel>
      {page === "folders" && (
        <>
          <div
            style={{
              display: "flex",
              color: "var(--color-grey)",
              fontSize: "14px",
              padding: "5px",
            }}
          >
            {formatMessage("cof.createnewobject.componentselection.message")}
          </div>
          <WithApiFileLocationProvider
            selectDepth="ITEM"
            cleanState={!loaded}
            getItems={async (path) => {
              const documentFolder = await fetchDocumentList(path);
              setState((s) => ({ ...s, loaded: true }));
              return mapDocumentPartsToFileLocations(documentFolder);
            }}
          >
            <DocumentLocationExplorer
              onSelectItem={onSelectPart}
              documentPartWithStatus={null}
              setDocumentPartWithStatus={() => {}}
            />
          </WithApiFileLocationProvider>
        </>
      )}
      {page === "outline" && <DocumentOutline selectedTreeItem={itemSelected} onItemClick={onOutlineItemClick} />}
      {page === "components" && (
        <ObjectComponentSearchPane
          selectedObjectComponentInfoItem={selectedObjectComponentInfoItem}
          onSelectObjectComponentInfoItem={(item) => setState({ ...state, selectedObjectComponentInfoItem: item })}
          documentId={outlineItemSelected.key}
        ></ObjectComponentSearchPane>
      )}
      <FooterPanel>
        <PrimaryButton
          data-testid="Back"
          className={ButtonVariants.white}
          text={formatMessage("cof.base.previous")}
          onClick={() => {
            if (page === "components") {
              setState((s) => ({
                ...s,
                page: "outline",
                outlineItemSelected: null,
                selectedObjectComponentInfoItem: null,
              }));
            } else if (page === "outline") {
              setState((s) => ({
                ...s,
                page: "folders",
                outlineItemSelected: null,
                selectedObjectComponentInfoItem: null,
                itemSelected: null,
              }));
            } else {
              onComponentSelected(undefined);
            }
          }}
          style={{ float: "left" }}
        />
        <PrimaryButton
          data-testid="select-component"
          id={"select-component"}
          text={selectButtonText}
          onClick={() => {
            if (page === "folders") {
              setState({ ...state, page: "outline" });
            } else if (page === "outline") {
              setState({ ...state, page: "components" });
            } else {
              onComponentSelected(selectedObjectComponentInfoItem);
            }
          }}
          disabled={selectButtonDisabled}
          className={ButtonVariants.primary}
          style={{ float: "right" }}
        />
      </FooterPanel>
    </Modal>
  );
};

interface ObjectComponentSearchPaneProps {
  selectedObjectComponentInfoItem?: ComponentReference;
  onSelectObjectComponentInfoItem: (item: ComponentReference) => void;
  documentId: string;
}

interface ObjectComponentSearchPaneState {
  searchValue: string;
  items: ComponentReference[];
}

const ObjectComponentSearchPane: React.FunctionComponent<ObjectComponentSearchPaneProps> = (props) => {
  const [state, setState] = useState<ObjectComponentSearchPaneState>({
    searchValue: "",
    items: [],
  });
  const { selectedObjectComponentInfoItem, onSelectObjectComponentInfoItem, documentId } = props;
  const { searchValue, items } = state;

  useEffect(() => {
    fetchDocumentComponents(documentId).then((components) => {
      if (components) {
        setState({
          ...state,
          items: components,
        });
      } else {
        setState({ ...state, items: [] });
      }
    });
  }, []);

  const filteredItems = items.filter((item) => item.objectName.toLowerCase().includes(searchValue.toLowerCase()));

  return (
    <div>
      <SearchBar onSearch={(query) => setState({ ...state, searchValue: query })} />
      <div style={{ height: "65vh", overflow: "scroll", overflowY: "auto", overflowX: "hidden" }}>
        {filteredItems.map((item) => (
          <ObjectComponentInfoItemRow
            key={item.contentControlId}
            item={item}
            selected={selectedObjectComponentInfoItem?.contentControlId == item.contentControlId}
            onSelectObjectComponentInfoItem={onSelectObjectComponentInfoItem}
          />
        ))}
      </div>
    </div>
  );
};

const ObjectComponentInfoItemRow = (props: {
  item: ComponentReference;
  selected: boolean;
  onSelectObjectComponentInfoItem: (item: ComponentReference) => void;
}) => {
  const { item, selected, onSelectObjectComponentInfoItem } = props;
  return (
    <div
      className={`component_item_row ${selected ? "component_item_row_selected" : ""}`}
      {...getHTMLElementAsButtonProps({
        onClick: () => onSelectObjectComponentInfoItem(item),
      })}
    >
      <Stack horizontal horizontalAlign="space-between">
        <div className={`${item.type} icon_class`}>
          {item.objectName}
          <span className="component_item_icon">{"  "}</span>
        </div>
      </Stack>
    </div>
  );
};
